import React from 'react';
import Container from '../container';
import styled, { css } from 'styled-components';
import {
  CaseStudy,
  CaseStudiesSliderCardInfoItem
} from '../../types/case-study';
import LazyImage from '../lazy-image';
import { getFluidGatsbyImage } from '../../utils/gatsby-storyblok-image-fixed';
import RichtextParser from '../../utils/richtext-parser';
import CtaWithArrow from '../cta-with-arrow';
import InfoItem from './InfoItem';
import mq from '../../utils/mq';

const CaseStudyProjectCardOuterContainer = styled.div`
  width: 100%;
  display: flex;
  justify-content: center;
  align-items: center;
  background: #f8f8f8;
  padding: 74px 0;
`;

const CaseStudyProjectCardContainer = styled.div`
  width: 100%;
  display: flex;
  background: #fff;
  position: relative;
  border-radius: var(--Radius-Radius-3, 16px);
  overflow: hidden;
  ${mq.mobile(css`
    flex-direction: column;
  `)};
`;

const ImageContainer = styled.div`
  width: 100%;
  max-width: 450px;
  flex-shrink: 0;
  position: relative;
`;

const ContentContainer = styled.div`
  width: 100%;
  padding: 60px 45px;
  ${mq.mobile(css`
    padding: 32px 16px;
  `)};
`;

const LabelContainer = styled.div`
  display: flex;
  gap: 8px;
  justify-content: flex-start;
  align-items: center;
  margin-bottom: 15px;
`;

const LabelText = styled.div`
  color: #161616;
  font-size: 18px;
  font-style: normal;
  font-weight: 700;
  line-height: 140%;
  letter-spacing: -0.36px;
`;

const LabelDecoration = styled.div`
  width: 18px;
  height: 2px;
  flex-shrink: 0;
  background: #8a55fb;
`;

const Title = styled.div`
  color: #161616;
  font-size: 42px;
  font-style: normal;
  font-weight: 700;
  line-height: 48px;
  margin-bottom: 24px;
  ${mq.mobile(css`
    font-size: 2rem;
    line-height: 125%;
  `)};
`;

const Description = styled.div`
  color: #666;
  font-size: 18px;
  font-style: normal;
  font-weight: 400;
  line-height: 160%;
  letter-spacing: -0.36px;
  ${mq.mobile(css`
    font-size: 1.1rem;
  `)};
`;

const InfoItems = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  grid-gap: 20px;
  margin-top: 24px;
  margin-bottom: 58px;
  ${mq.mobile(css`
    margin-top: 16px;
    margin-bottom: 32px;
  `)};
`;

interface CaseStudyProjectCardProps {
  label: string;
  caseStudy: CaseStudy[];
  infoItems: CaseStudiesSliderCardInfoItem[];
}

export default function CaseStudyProjectCard({
  label,
  caseStudy,
  infoItems
}: CaseStudyProjectCardProps) {
  if (caseStudy?.length < 1) {
    return null;
  }
  const caseStudyData = caseStudy[0] as CaseStudy;
  return (
    <CaseStudyProjectCardOuterContainer>
      <Container>
        <CaseStudyProjectCardContainer>
          <ImageContainer>
            <LazyImage
              fluid={getFluidGatsbyImage(caseStudyData?.content?.gridImage)}
              width={'100%'}
              height={'100%'}
              style={{ objectFit: 'cover' }}
            />
          </ImageContainer>
          <ContentContainer>
            <LabelContainer>
              <LabelText>Next Case Study</LabelText>
              <LabelDecoration />
            </LabelContainer>
            <Title>{caseStudyData?.content?.title}</Title>
            <Description
              dangerouslySetInnerHTML={
                caseStudyData?.content?.about[0]?.infoParagraph
                  ? {
                      __html: RichtextParser(
                        caseStudyData?.content?.about[0]?.infoParagraph
                      )
                    }
                  : { __html: '' }
              }
            />
            <InfoItems>
              {infoItems?.map((infoItem, i) => {
                return <InfoItem key={i} {...infoItem} />;
              })}
            </InfoItems>

            <CtaWithArrow
              label="View Case Study"
              link={`/${caseStudyData?.full_slug}`}
            />
          </ContentContainer>
        </CaseStudyProjectCardContainer>
      </Container>
    </CaseStudyProjectCardOuterContainer>
  );
}
